'use client';

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { RosterStarProps, RosterStar } from './roaster-star';

const MAX_STAR = 5;
const DEFAULT_GAP = 4;

interface AutofitRosterStarProps extends RosterStarProps {
  maxSize?: number;
  gap?: number;

  fullStarSize?: number;
}

const useObserveWindowSize = (callback: () => void) => {
  const ref = useRef<{ callback: () => void }>({ callback });
  ref.current.callback = callback;
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      ref.current.callback();
    });
    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);
};

const getSize = (width: number, gap: number, maxStar: number) => {
  return Math.floor((width - gap * (maxStar - 1)) / maxStar);
};

export const AutofitRosterStar: FC<AutofitRosterStarProps> = ({
  rate,
  hollowStar,
  level,
  size: originSize,
  maxSize,
  fullStarSize,
  gap = DEFAULT_GAP,
  className,
}) => {
  const [size, setSize] = useState<number>();
  const ref = useRef<HTMLDivElement>(null);
  const totalStar = rate + hollowStar;
  const isFullStar = totalStar >= 6;

  useObserveWindowSize(() => {
    const width = ref.current?.clientWidth;
    if (width == null) return;
    const size = getSize(width, gap, MAX_STAR);
    setSize(size);
  });

  const actualSize = useMemo(() => {
    if (isFullStar) {
      return fullStarSize ?? originSize;
    }
    if (size == null) return undefined;
    if (maxSize) {
      return Math.min(size, maxSize);
    }
    return size;
  }, [isFullStar, originSize, size, maxSize, fullStarSize]);
  return (
    <div ref={ref} className="w-full">
      {size != null && (
        <RosterStar
          className={className}
          ref={ref}
          rate={rate}
          hollowStar={hollowStar}
          level={level}
          size={actualSize}
        />
      )}
    </div>
  );
};
